import React, { useContext } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useLocation } from 'react-router';
import { FormApi } from 'final-form';
import { History, Location } from 'history';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'CypressId';

import { VehicleLookupContext } from 'Context/vehicleLookup';
import type { VehicleLookupContextProps } from 'Context/vehicleLookup.types';
import type { VehicleContextProps } from 'Context/vehicles';
import { VehicleContext } from 'Context/vehicles';

import { getCategoryForSticky, STICKY_VRM_EVENTS } from 'Utilities/analytics';
import { unprettyVRM } from 'Utilities/formValidators';
import { vehicleType } from 'Utilities/helpers';
import { applyCypressData } from 'Utilities/index';

import { VRMInput } from 'Containers/app/fastlane/form/vrm/VRMInput';

import Texts from '../../../texts.json';

const T = new MDText(Texts);

const SELL_MY_CAR_URL = '/sell-my-car';
const formatVehicleLookupUrl = (value: string) => `/${value.replace(/ /g, '').toUpperCase()}`;

type StickyVrmVersion = 'new' | 'old' | undefined;

export type VrmCheckerBaseProps = {
  analyticsEvents?: {
    invalidVrmWarningDisplayed?: () => void;
    onButtonClick?: Function;
    onInputFocus?: () => void;
    vrmSubmittedSuccessfully?: () => void;
  };
  buttonProps?: {
    className?: string;
    label?: React.ReactNode;
    labelChildren?: React.ReactNode;
    showLabel?: boolean;
  };
  chevron?: boolean;
  className?: string;
  directToSellerPage?: boolean;
  disabled?: boolean;
  e2eName?: string;
  handleVrmSubmit?: Function;
  hideIntent?: boolean;
  history: History;
  home?: boolean;
  id?: string;
  inputClassName?: string;
  isWidget?: boolean;
  large?: boolean;
  location: Location;
  parentKey?: string;
  required?: boolean;
  showLabel?: boolean;
  snowplowEvents?: boolean;
  value?: StickyVrmVersion;
  vehicleContext: VehicleContextProps;
  vehicleLookupContext: VehicleLookupContextProps;
};

type VrmCheckerBaseState = {
  html5Validation: boolean;
  valueIsQuickVRM: boolean;
};

type FormValues = {
  vrm: StickyVrmVersion;
}

export class VrmCheckerBase extends React.Component<VrmCheckerBaseProps, VrmCheckerBaseState> {
  constructor(props: VrmCheckerBaseProps) {
    super(props);

    const { value } = this.props;

    this.state = {
      html5Validation: true,
      valueIsQuickVRM: !!value,
    };
  }

  componentDidMount() {
    this.setState({ html5Validation: false });
  }

  shouldComponentUpdate(nextProps: VrmCheckerBaseProps, nextState: VrmCheckerBaseState) {
    const {
      buttonProps, ...nextPropsValues
    } = nextProps;
    const {
      buttonProps: b, ...propsValues
    } = this.props;
    return !(JSON.stringify(nextState) === JSON.stringify(this.state)
      && JSON.stringify(nextPropsValues) === JSON.stringify(propsValues));
  }

  handleVrmSubmitToSellerPage = (value: FormValues['vrm'], reset: () => void) => {
    const {
      history,
      isWidget,
      location,
      snowplowEvents,
      vehicleContext,
      vehicleLookupContext,
    } = this.props;
    const { vehicleLookupActions } = vehicleLookupContext;
    const { vehicleState: vehicles } = vehicleContext;

    const sellMyCarPages = history.location.pathname.includes(SELL_MY_CAR_URL);
    const unprettyVrm = unprettyVRM(value);
    const existingVRM = vehicles.some((item: { vrm: string }) => item.vrm === unprettyVrm);

    const { pathname } = location;

    if (existingVRM) {
      const url = formatVehicleLookupUrl(String(value));

      if (snowplowEvents) {
        if (isWidget) {
          STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: value,
            url,
          });
        } else {
          STICKY_VRM_EVENTS.STICKY_VRM_BANNER_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: value,
            url,
          });
        }
      }

      return history.push(url);
    }

    if (snowplowEvents) {
      if (isWidget) {
        STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_OLD_CTA_CLICK({
          category: getCategoryForSticky(pathname),
          label: value,
          url: SELL_MY_CAR_URL,
        });
      } else {
        STICKY_VRM_EVENTS.STICKY_VRM_BANNER_OLD_CTA_CLICK({
          category: getCategoryForSticky(pathname),
          label: value,
          url: SELL_MY_CAR_URL,
        });
      }
    }

    vehicleLookupActions.add({ vehicle: { vrm: value } });

    if (sellMyCarPages) {
      reset();
    }

    return history.push(SELL_MY_CAR_URL);
  };

  handleVrmSubmit = (value?: FormValues['vrm']) => {
    const {
      history,
      isWidget,
      location,
      snowplowEvents,
    } = this.props;

    const { pathname } = location;

    if (value) {
      const url = formatVehicleLookupUrl(value);

      if (snowplowEvents) {
        if (isWidget) {
          STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: value,
            url,
          });
        } else {
          STICKY_VRM_EVENTS.STICKY_VRM_BANNER_OLD_CTA_CLICK({
            category: getCategoryForSticky(pathname),
            label: value,
            url,
          });
        }
      }

      history.push(url);
    }
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = this.props;
    this.setState({
      valueIsQuickVRM: e.target.value.toUpperCase() === value,
    });
  };

  handleChangeCarClick = (form: FormApi<FormValues>) => (e: React.MouseEvent) => {
    e.preventDefault();

    form.change('vrm', '' as FormValues['vrm']);
    this.setState({
      valueIsQuickVRM: false,
    });
  };

  handleSubmit = ({ vrm } : FormValues, form: FormApi<FormValues>) => {
    const {
      analyticsEvents, directToSellerPage, handleVrmSubmit,
    } = this.props;

    const reset = () => {
      form.change('vrm', '' as FormValues['vrm']);
    };

    analyticsEvents?.vrmSubmittedSuccessfully?.();
    if (handleVrmSubmit) {
      return handleVrmSubmit(vrm, reset);
    }
    if (directToSellerPage) {
      return this.handleVrmSubmitToSellerPage(vrm, reset);
    }
    return this.handleVrmSubmit(vrm);
  };

  render() {
    const {
      analyticsEvents,
      buttonProps,
      className,
      disabled,
      e2eName,
      hideIntent,
      home,
      id,
      inputClassName,
      isWidget,
      large,
      location,
      required = true,
      showLabel,
      snowplowEvents,
      value,
    } = this.props;

    const { valueIsQuickVRM } = this.state;

    let {
      className: buttonClassName,
      label,
      labelChildren,
      ...restButtonProps
    } = buttonProps || {};

    label = home ? T.translate('components.vrmChecker.buttonLabelValue') : T.translate('components.vrmChecker.buttonLabel', {
      vehicle: vehicleType(location.pathname),
    });
    label = (value && valueIsQuickVRM) ? T.translate('components.vrmChecker.loggedIn.buttonLabel') : label;
    label = labelChildren || buttonProps?.label || label;

    const { pathname } = location;
    const isUlezSticky = getCategoryForSticky(pathname) === 'ulez';

    const onInputFocus = () => {
      if (snowplowEvents) {
        if (isWidget) {
          STICKY_VRM_EVENTS.STICKY_VRM_WIDGET_INPUT_FOCUS({
            category: getCategoryForSticky(pathname),
          });
        } else {
          STICKY_VRM_EVENTS.STICKY_VRM_BANNER_INPUT_FOCUS({
            category: getCategoryForSticky(pathname),
            label: (isUlezSticky ? 'sticky' : 'old') as StickyVrmVersion,
            ...(isUlezSticky && { name: 'vrm_input' }),
          });
        }
      } else {
        analyticsEvents?.onInputFocus?.();
      }
    };

    return (
      <Form<FormValues>
        validateOnBlur
        initialValues={{ vrm: value }}
        onSubmit={this.handleSubmit}
        render={({
          form, handleSubmit,
        }) => (
          <form
            className={className}
            noValidate={!this.state.html5Validation}
            onSubmit={(event) => {
              // Reset the same validate function as we set initally
              // for the side effect that it will re-run validation.
              // We need to do this as react-final-form does not run validation on submit
              // when validateOnBlur is true
              // https://github.com/final-form/final-form/issues/213
              form.setConfig('validate', undefined);
              handleSubmit(event);
            }}
          >
            <VRMInput
              onChange={this.handleInputChange}
              onInputBlur={() => {
                analyticsEvents?.invalidVrmWarningDisplayed?.();
              }}
              onInputFocus={() => onInputFocus()}
              vrmId={id}
              {...{ className: inputClassName, disabled, e2eName, hideIntent, large, required }}
            />
            <div className={buttonClassName}>
              <Button
                {...applyCypressData(cypressIds.buttons.vrmButton) }
                {...restButtonProps}
                fullWidth
                as="button"
                data-e2ename={`${e2eName} button`}
                disabled={disabled}
                icon="arrow"
                label={label}
                onClick={() => {
                  analyticsEvents?.onButtonClick?.();
                }}
                showLabel={showLabel}
                size={large ? 'large' : 'medium'}
                type="submit"
              />
            </div>
            {value && valueIsQuickVRM
              ? (
                <span>
                  {T.translate('components.vrmChecker.loggedIn.changeCarLink', {
                    changeCarLink: <a href="#changecar" onClick={this.handleChangeCarClick(form)}>{T.translate('components.vrmChecker.loggedIn.changeCarLinkText')}</a>,
                  })}
                </span>
              )
              : null}
          </form>
        )}
      />
    );
  }
}

// We have removed the original HOC connect approach.
// Even removing "connect" still passed context as props
// so using modern hooks, this is not an issue anymore
const VrmCheckerWrapper = (
  {
    ref,
    ...props
  }: Partial<VrmCheckerBaseProps> & {
    ref?: React.Ref<VrmCheckerBase>;
  },
) => {
  const history = useHistory();
  const location = useLocation();
  const vehicleContext = useContext(VehicleContext);
  const vehicleLookupContext = useContext(VehicleLookupContext);

  return (
    <VrmCheckerBase
      ref={ref}
      {...props}
      {...{
        history,
        location,
        vehicleContext,
        vehicleLookupContext,
      }}
    />
  );
};

export default VrmCheckerWrapper;
